// The members table: one virtualized DataTable with team headings between the people (TeamGroupCard on the iPhone).
// Sorting is done in members.ts (within each team), so the table runs in manual-sorting mode and only reports which
// header was clicked. A person row opens their day (/team/:profileId); admins get a "…" menu per row.

import { Ellipsis, SearchX, Users } from 'lucide-react';
import { useMemo, type ReactNode } from 'react';
import type { VisibilityState } from '@tanstack/react-table';
import {
  Avatar,
  Chip,
  ColorChip,
  ColorDot,
  DataTable,
  DropdownMenu,
  EmptyState,
  GT_COLORS,
  IconButton,
  Skeleton,
  useMediaQuery,
  type DataTableColumn,
  type MenuEntry,
  type SortingState,
} from '@/ui';
import { MEMBER_SORT_KEYS, type MemberSort, type MemberSortKey, type MemberTableRow, type PersonRow } from './members';
import { AccountBadge, LastSeen, NewerAppBadge, RoleChip } from './parts';

export interface MembersTableProps {
  rows: readonly MemberTableRow[];
  sort: MemberSort;
  onSortChange: (sort: MemberSort) => void;
  now: Date;
  /** The roster is loading: the Account column shows placeholders. */
  rosterLoading: boolean;
  onOpen: (row: PersonRow) => void;
  /** Row menu (admins); omit for a read-only table. */
  actionsFor?: (row: PersonRow) => MenuEntry[];
  /** Rows come with team headings: the Team column then repeats the heading and gives way on narrow screens. */
  grouped: boolean;
  /** Search text, for the empty state. */
  query: string;
  onClearQuery: () => void;
  className?: string;
}

const isPerson = (row: MemberTableRow): row is PersonRow => row.kind === 'person';

function personCell(render: (row: PersonRow) => ReactNode) {
  return ({ row }: { row: { original: MemberTableRow } }) => (isPerson(row.original) ? render(row.original) : null);
}

export function MembersTable({
  rows,
  sort,
  onSortChange,
  now,
  rosterLoading,
  onOpen,
  actionsFor,
  grouped,
  query,
  onClearQuery,
  className,
}: MembersTableProps) {
  // Below 1280px (tablets, a narrow window) the table drops the columns the row already implies, so the numbers stay
  // on screen without scrolling sideways: Title, and Team when the team headings show it.
  const wide = useMediaQuery('(min-width: 1280px)');
  const columnVisibility = useMemo<VisibilityState>(
    () => (wide ? {} : { title: false, ...(grouped ? { team: false } : {}) }),
    [wide, grouped],
  );
  const columns = useMemo<DataTableColumn<MemberTableRow>[]>(() => {
    const cols: DataTableColumn<MemberTableRow>[] = [
      {
        id: 'name',
        header: 'Name',
        // Accessors only make the headers sortable; the order itself comes from members.ts.
        accessorFn: (r) => (isPerson(r) ? r.name : ''),
        size: 260,
        meta: { grow: 2 },
        cell: ({ row }) => {
          const r = row.original;
          if (!isPerson(r)) return <GroupHeading row={r} />;
          return (
            <div className="flex min-w-0 items-center gap-3">
              <Avatar
                name={r.name}
                colorHex={r.user ? r.user.colorHex : null}
                size={32}
                presence={r.user ? r.presence : null}
                label={null}
              />
              <div className="min-w-0">
                <div className="flex min-w-0 items-center gap-1.5">
                  <span className="truncate font-semibold">{r.name || 'Unnamed'}</span>
                  {r.isMe && (
                    <Chip size="xs" color={GT_COLORS.navy}>
                      You
                    </Chip>
                  )}
                  {r.newerApp && <NewerAppBadge />}
                </div>
                <div className="truncate text-xs text-fg-muted">
                  {/* The title moves under the name when its column gives way. */}
                  {!wide && r.title ? `${r.title} · ` : ''}
                  {r.email || (r.user ? 'No email' : 'No profile')}
                </div>
              </div>
            </div>
          );
        },
      },
      {
        id: 'title',
        header: 'Title',
        accessorFn: (r) => (isPerson(r) ? r.title : ''),
        size: 150,
        meta: { grow: 1 },
        cell: personCell((r) => (r.title ? <span className="truncate">{r.title}</span> : <span className="text-fg-subtle">—</span>)),
      },
      {
        id: 'team',
        header: 'Team',
        accessorFn: (r) => (isPerson(r) ? r.teamName : ''),
        size: 150,
        cell: personCell((r) =>
          r.team ? (
            <ColorChip color={r.team.colorHex} className="max-w-full">
              {r.team.name}
            </ColorChip>
          ) : (
            <span className="text-fg-subtle">No team</span>
          ),
        ),
      },
      {
        id: 'role',
        header: 'Role',
        accessorFn: (r) => (isPerson(r) ? r.role : ''),
        size: 110,
        cell: personCell((r) => <RoleChip role={r.role} />),
      },
      {
        id: 'account',
        header: 'Account',
        accessorFn: (r) => (isPerson(r) ? (r.account?.status ?? '') : ''),
        size: 150,
        cell: personCell((r) =>
          r.account ? (
            <AccountBadge status={r.account.status} />
          ) : rosterLoading ? (
            <Skeleton className="h-4" width={80} />
          ) : (
            <span className="text-fg-subtle" title="Account status isn't available right now">
              —
            </span>
          ),
        ),
      },
      {
        id: 'lastSeen',
        header: 'Last seen',
        accessorFn: (r) => (isPerson(r) ? (r.lastSeenAt?.getTime() ?? 0) : 0),
        size: 170,
        cell: personCell((r) =>
          r.user ? <LastSeen at={r.lastSeenAt} presence={r.presence} now={now} /> : <span className="text-fg-subtle">—</span>,
        ),
      },
      {
        id: 'stores',
        header: 'Stores',
        accessorFn: (r) => (isPerson(r) ? r.storesOwned : 0),
        size: 90,
        meta: { align: 'right' },
        cell: personCell((r) => <span className="tabular-nums">{r.storesOwned.toLocaleString()}</span>),
      },
      {
        id: 'visits',
        header: 'Visits this week',
        accessorFn: (r) => (isPerson(r) ? r.weekVisits : 0),
        size: 140,
        meta: { align: 'right' },
        cell: personCell((r) => <span className="tabular-nums">{r.weekVisits.toLocaleString()}</span>),
      },
    ];
    if (actionsFor) {
      cols.push({
        id: 'actions',
        header: () => <span className="sr-only">Actions</span>,
        enableSorting: false,
        size: 56,
        meta: { align: 'center' },
        cell: personCell((r) => {
          const items = actionsFor(r);
          if (items.length === 0) return null;
          return (
            <DropdownMenu
              label={`Actions for ${r.name || 'this person'}`}
              placement="bottom-end"
              items={items}
              trigger={<IconButton label={`Actions for ${r.name || 'this person'}`} icon={<Ellipsis />} size="sm" />}
            />
          );
        }),
      });
    }
    return cols;
  }, [now, rosterLoading, actionsFor, wide]);

  const sorting = useMemo<SortingState>(() => [{ id: sort.key, desc: sort.desc }], [sort]);

  const empty =
    query.trim() !== '' ? (
      <EmptyState
        compact
        icon={<SearchX />}
        title="No matches"
        message="Try another name, email or team."
        action={
          <button type="button" onClick={onClearQuery} className="text-sm font-semibold text-link hover:underline">
            Clear search
          </button>
        }
      />
    ) : (
      <EmptyState compact icon={<Users />} title="No team members yet" message="People appear here once they have a profile in Team." />
    );

  return (
    <DataTable<MemberTableRow>
      label="Team members"
      data={rows}
      columns={columns}
      getRowId={(r) => r.id}
      manualSorting
      sorting={sorting}
      columnVisibility={columnVisibility}
      onSortingChange={(next) => {
        const first = next[0];
        const key = first && (MEMBER_SORT_KEYS as readonly string[]).includes(first.id) ? (first.id as MemberSortKey) : 'name';
        onSortChange({ key, desc: first ? first.desc : false });
      }}
      rowHeight={52}
      onRowClick={(r) => {
        if (isPerson(r) && r.profileId) onOpen(r);
      }}
      getRowClassName={(r) =>
        isPerson(r) ? (r.profileId ? undefined : 'cursor-default!') : 'cursor-default! bg-surface-2! text-fg-muted'
      }
      emptyState={empty}
      className={className}
    />
  );
}

function GroupHeading({ row }: { row: Extract<MemberTableRow, { kind: 'group' }> }) {
  return (
    <div className="flex min-w-0 items-center gap-2 text-[11px] font-semibold tracking-[0.05em] uppercase">
      {row.color ? <ColorDot color={row.color} size={8} /> : null}
      <span className="truncate text-fg">{row.title}</span>
      {row.managerName && <span className="truncate font-normal tracking-normal normal-case">· {row.managerName}</span>}
      <span className="ml-auto pl-2 tabular-nums">{row.count}</span>
    </div>
  );
}
